import { createAsyncThunk } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import { readAllPlacesAtPlan } from 'src/api/places';
import { BackgroundData, getPlanBackground } from 'src/api/plan';
import {
  checkPlaces,
  createSpace,
  CreateSpaceParams,
  deleteSpace,
  findSpaces,
  FindSpacesParams,
  PlaceSpace,
  updateSpace,
  UpdateSpaceParams,
} from 'src/api/spaces';
import { SpaceInfo } from 'src/api/types';
import { BookingObject } from 'src/types';
import { defaultServerError } from 'src/utils/consts';
import { toBookingObject } from 'src/utils/toBookingObject';

import { messagesActions } from '../messages/slice';
import { StoreState, ThunkConfig } from '../types';
import { getEmployeeNumber } from '../user/selectors';
import { fetchUserPermissions } from '../user/services';

import { spacesFirstPageNumber } from './initialState';
import { getSpacesState } from './selectors';
import { spacesActions } from './slice';

export const spacesPageSize = 15;

export const fetchSpaces = createAsyncThunk<
  SpaceInfo[] | void,
  boolean,
  ThunkConfig<string>
>(
  'spaces/fetchSpaces',
  async (fromStart, { dispatch, getState, rejectWithValue, requestId }) => {
    try {
      const state = getState() as StoreState;
      const {
        search,
        hasMore: hasMoreFromState,
        pageNumber: pageNumberFromState,
        currentRequestId,
      } = getSpacesState(state);

      const hasMore = fromStart ? true : hasMoreFromState;
      const pageNumber = fromStart
        ? spacesFirstPageNumber
        : pageNumberFromState;

      if (!hasMore || currentRequestId !== requestId) return;

      const params: FindSpacesParams = {
        pageSize: spacesPageSize,
        pageNumber,
        searchText: search || undefined,
      };

      const response = await findSpaces(params);

      return response;
    } catch (e) {
      dispatch(messagesActions.show({ message: defaultServerError }));
      return rejectWithValue((e as AxiosError).message);
    }
  },
);

export const removeSpace = createAsyncThunk<void, number, ThunkConfig<string>>(
  'spaces/removeSpace',
  async (id, { dispatch, rejectWithValue }) => {
    try {
      await deleteSpace(id);
      await dispatch(fetchUserPermissions());
      await dispatch(fetchSpaces(true));
    } catch (e) {
      dispatch(messagesActions.show({ message: defaultServerError }));
      return rejectWithValue((e as AxiosError).message);
    }
  },
);

export const addSpace = createAsyncThunk<
  void,
  CreateSpaceParams,
  ThunkConfig<string>
>(
  'spaces/addSpace',
  async (params, { getState, dispatch, rejectWithValue }) => {
    try {
      const state = getState() as StoreState;
      const employeeNumber = getEmployeeNumber(state);

      await createSpace(params);
      if (employeeNumber && employeeNumber === params.administrator)
        await dispatch(fetchUserPermissions());
      await dispatch(spacesActions.closeSpace());
      await dispatch(fetchSpaces(true));
    } catch (e) {
      dispatch(messagesActions.show({ message: defaultServerError }));
      return rejectWithValue((e as AxiosError).message);
    }
  },
);

export const changeSpace = createAsyncThunk<
  void,
  UpdateSpaceParams,
  ThunkConfig<string>
>(
  'spaces/changeSpace',
  async (params, { getState, dispatch, rejectWithValue }) => {
    try {
      const state = getState() as StoreState;

      const employeeNumber = getEmployeeNumber(state);
      const { space } = getSpacesState(state);

      await updateSpace(params);
      if (
        employeeNumber &&
        space?.administrator?.employeeNumber !== params.administrator &&
        (params.administrator === employeeNumber ||
          space?.administrator?.employeeNumber === employeeNumber)
      )
        await dispatch(fetchUserPermissions());

      await dispatch(spacesActions.closeSpace());
      await dispatch(fetchSpaces(true));
    } catch (e) {
      dispatch(messagesActions.show({ message: defaultServerError }));
      return rejectWithValue((e as AxiosError).message);
    }
  },
);

export interface SpaceMapData {
  backgroundData: BackgroundData | undefined;
  bookingObjects: Map<string, BookingObject>;
  relatedSpaces: Map<number, SpaceInfo>;
}

export const fetchSpaceMapData = createAsyncThunk<
  SpaceMapData,
  number,
  ThunkConfig<string>
>(
  'spaces/fetchSpaceMapData',
  async (floorId, { dispatch, rejectWithValue }) => {
    try {
      const [places, backgroundData] = await Promise.all([
        readAllPlacesAtPlan(floorId),
        getPlanBackground(floorId),
      ]);
      const bookingObjects = new Map<string, BookingObject>();

      for (const place of places.places) {
        const bookingObject = toBookingObject(place);
        bookingObjects.set(bookingObject.mapPlaceId, bookingObject);
      }

      const relatedSpaces = new Map<number, SpaceInfo>();

      for (const space of places.relatedSpaces) {
        relatedSpaces.set(space.spaceId, space);
      }

      return { bookingObjects, backgroundData, relatedSpaces };
    } catch (e) {
      dispatch(messagesActions.show({ message: defaultServerError }));
      return rejectWithValue((e as AxiosError).message);
    }
  },
);

export const validateSpace = createAsyncThunk<
  PlaceSpace[],
  number[],
  ThunkConfig<string>
>('spaces/validateSpace', async (ids, { dispatch, rejectWithValue }) => {
  try {
    return checkPlaces(ids);
  } catch (e) {
    dispatch(messagesActions.show({ message: defaultServerError }));
    return rejectWithValue((e as AxiosError).message);
  }
});
